const MAX_DIAGNOSTIC_BYTES = 1024;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const UTC_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?Z$/;

const ALLOWED = Object.freeze({
  operation: new Set(['appointment_create', 'unknown']),
  stage: new Set(['request_validation', 'patient_prepare', 'provider_create', 'association_persistence', 'unknown']),
  outcome: new Set(['rejected', 'reconciliation_required', 'unknown']),
  code: new Set([
    'dentally_appointment_creation_unresolved',
    'dentally_create_outcome_unknown',
    'dentally_create_rejected',
    'dentally_create_request_invalid',
    'dentally_create_rejection_release_failed',
    'dentally_link_persistence_failed',
    'dentally_patient_export_in_progress',
    'dentally_patient_export_unresolved',
    'dentally_patient_prepare_outcome_unknown',
    'dentally_patient_prepare_rejected',
    'unknown',
  ]),
  provider_category: new Set([
    'validation',
    'authorization',
    'rate_limit',
    'network',
    'provider_unavailable',
    'malformed_response',
    'association_failure',
    'unknown',
  ]),
  durable_state: new Set(['none', 'in_progress', 'unknown', 'active']),
});

const isPlainObject = (value) => (
  value !== null
  && typeof value === 'object'
  && !Array.isArray(value)
  && Object.getPrototypeOf(value) === Object.prototype
);

const utf8ByteLength = (value) => Array.from(value).reduce((total, character) => {
  const codePoint = character.codePointAt(0);
  if (codePoint <= 0x7f) return total + 1;
  if (codePoint <= 0x7ff) return total + 2;
  if (codePoint <= 0xffff) return total + 3;
  return total + 4;
}, 0);

export function projectDentallyBookingDiagnostic(value) {
  if (!isPlainObject(value) || value.schema_version !== 1) return null;

  try {
    const rawDiagnostic = JSON.stringify(value);
    if (typeof rawDiagnostic !== 'string'
      || utf8ByteLength(rawDiagnostic) > MAX_DIAGNOSTIC_BYTES) {
      return null;
    }
  } catch {
    return null;
  }

  if (!UUID_PATTERN.test(value.diagnostic_id || '')) return null;
  if (typeof value.retryable !== 'boolean') return null;
  if (!UTC_TIMESTAMP_PATTERN.test(value.occurred_at || '')) return null;

  const enumFields = ['operation', 'stage', 'outcome', 'code', 'provider_category', 'durable_state'];
  if (enumFields.some((field) => typeof value[field] !== 'string' || !ALLOWED[field].has(value[field]))) {
    return null;
  }

  const projected = {
    schema_version: 1,
    diagnostic_id: value.diagnostic_id,
    operation: value.operation,
    stage: value.stage,
    outcome: value.outcome,
    code: value.code,
    retryable: value.retryable,
    provider_category: value.provider_category,
    durable_state: value.durable_state,
    occurred_at: value.occurred_at,
  };

  return JSON.stringify(projected).length <= MAX_DIAGNOSTIC_BYTES ? projected : null;
}
